// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Lakko Contributors

/**
 * ADR-0047 / UX spec §2, §3, §6 — the composer's recording surfaces: the
 * record button, the recording bar that replaces the text field, the round
 * video-note preview, and the permission / hint notices.
 *
 * Pure renderers of a `Recorder` (`recording-controller.ts`); no rule lives
 * here.
 */

import { createEffect, createSignal, For, onCleanup, Show, type JSX } from "solid-js";
import { Icon } from "../ui/icons";
import { t } from "../l10n";

import {
  formatClock,
  formatTotalClock,
  capFor,
  spokenDuration,
  LOCK_SLIDE_PX,
  type RecState,
} from "./recording-machine";
import { SILENT_MIC_NOTICE } from "./microphone-silence";
import { deniedCopy, noticeCopy, type Recorder } from "./recording-controller";
import { barIsPlayed, waveformBars } from "./waveform";

function holding(s: RecState): boolean {
  return s.phase === "recording" || s.phase === "requesting";
}

/**
 * The right-hand composer button when the field and tray are empty.
 *
 * UX spec §3.1 — ONE visible button, carrying the current mode's glyph. A tap
 * switches the mode, a hold records; both are the machine's rules, reached
 * through the pointer handlers. What sits beside it is not a second button but
 * the mode switch a keyboard or a screen reader needs, which carries no ink:
 * hold and slide are gestures those users do not have, so without it the
 * camera would be unreachable for them.
 */
export function RecordButton(props: { recorder: Recorder }): JSX.Element {
  const r = props.recorder;
  const isVideo = (): boolean => r.mode() === "video_note";
  const label = (): string => (isVideo() ? t("chat.record.video") : t("chat.record.voice"));
  const switchLabel = (): string =>
    isVideo() ? t("chat.record.switch_to_voice") : t("chat.record.switch_to_video");
  return (
    <span
      style={{
        display: "inline-flex",
        "align-items": "stretch",
        // The anchor the lock target is positioned against (§2.5).
        position: "relative",
        // Hidden, never unmounted, once the take no longer needs it: the
        // button may still hold the pointer capture of the gesture that
        // locked the take.
        visibility: ["locked", "finalizing", "preview"].includes(r.state().phase) ? "hidden" : "visible",
      }}
      title={
        r.disabledReason() ??
        (isVideo() ? t("chat.record.video_tooltip") : t("chat.record.voice_tooltip"))
      }
    >
      <button
        type="button"
        class="lk-icon-button lakko-record-btn"
        aria-label={label()}
        aria-disabled={r.disabledReason() !== null}
        disabled={r.disabledReason() !== null}
        data-active={holding(r.state())}
        onPointerDown={(e) => r.onPointerDown(e)}
        onPointerMove={(e) => r.onPointerMove(e)}
        onPointerUp={(e) => r.onPointerUp(e)}
        onPointerCancel={() => r.onPointerCancel()}
        onContextMenu={(e) => e.preventDefault()}
        onClick={(e) => {
          // A pointer press is handled by the pointer events above, where a
          // short one switches the mode instead of recording (§3.1); only a
          // keyboard / assistive-technology activation (detail 0) lands here,
          // and it starts a locked recording (UX spec §7).
          if (e.detail === 0) r.onKeyboardActivate();
        }}
      >
        <Icon name={isVideo() ? "video" : "mic"} size={20} />
      </button>
      {/* §3.1 — the second action on the same control, for the people who
          have no hold gesture. Visually hidden so the composer reads as one
          button, but in the tab order and the accessibility tree, and it does
          become visible while it holds keyboard focus: a focus stop nobody can
          see is worse than the extra glyph the spec was avoiding. */}
      <button
        type="button"
        class="lakko-visually-hidden lakko-mode-switch-a11y"
        aria-label={switchLabel()}
        // Its own tooltip, or the wrapper's would describe the recorder while
        // this is the control being pointed at once focus reveals it.
        title={switchLabel()}
        // One button, one disabled state: whatever takes the visible glyph out
        // of use - a take in progress, a call holding the microphone, a browser
        // that cannot record - takes this with it, because a mode to record in
        // is worth nothing while there is nothing to record with.
        disabled={recorderBusy(r) || r.disabledReason() !== null}
        onClick={() => r.toggleMode()}
      >
        {switchLabel()}
      </button>
      <LockTarget recorder={r} />
    </span>
  );
}

/**
 * §2.5 / §3.1 — the target a held take slides UP onto to lock, drawn above the
 * record button exactly as iOS and Android draw it, and for the same reason: a
 * pointer slides onto something it can see, and a sentence naming a direction
 * is not a destination. It is the mouse pointer's affordance as much as the
 * thumb's, which is why the spec asks for it on this client too.
 *
 * Shown only while a take is being HELD (`recording`): `pressing` has not
 * decided whether it is a tap yet — drawing a lock target over a tap is the
 * flicker §3.1 was written against — and by `locked` the thing it invited has
 * happened, with the button itself already hidden.
 *
 * It never takes pointer events. The gesture it describes runs UNDER it: the
 * pointer that is climbing towards it is captured by the record button, and a
 * target that swallowed a `pointermove` would break the very slide it exists to
 * invite.
 *
 * Placement is not decoration. `LOCK_SLIDE_PX` is what locks the take, so the
 * capsule sits just far enough up that a pointer travelling that distance lands
 * inside it — the natives' ~64-70 px for the same arithmetic.
 */
function LockTarget(props: { recorder: Recorder }): JSX.Element {
  const s = (): RecState => props.recorder.state();
  // How far up the pointer has climbed. The machine locks the instant this
  // reaches `-LOCK_SLIDE_PX`, so a `recording` state NEVER carries a `dy` at or
  // past the threshold: "the pointer is standing on the target" is not a state
  // that exists. What can be shown is the approach, and it is the honest
  // signal — arriving at the capsule IS the lock.
  /**
   * A pointer gesture is live: the take is being held, or the device it will
   * record from is still opening under a finger that is already down.
   *
   * `requesting` is in it because the hold now spends its first moments there
   * waiting for the microphone, and a target that waited for the first
   * captured sample would flicker in under a thumb already climbing towards
   * it — while the machine answers that climb on arrival, so the affordance
   * would be inviting a lock that had already happened. A KEYBOARD activation
   * passes through the same phase and is excluded: it has no pointer, so there
   * is no slide for the target to invite.
   */
  const gesturing = (): boolean => {
    const st = s();
    return st.phase === "recording" || (st.phase === "requesting" && st.pointer !== "keyboard");
  };
  const climb = (): number => {
    const st = s();
    return gesturing() && "dy" in st ? Math.min(0, st.dy) : 0;
  };
  return (
    <Show when={gesturing()}>
      <span
        class="lakko-lock-target"
        data-near={climb() <= -LOCK_SLIDE_PX / 2}
        aria-hidden="true"
      >
        <Icon name="lock" size={16} />
        <Icon name="chevron" size={16} class="lakko-lock-up" />
      </span>
    </Show>
  );
}

/**
 * `true` while the recording bar replaces the text field.
 *
 * `pressing` is deliberately NOT busy (UX spec §3.1): a press that has not yet
 * lasted the hold threshold may still turn out to be a tap on the mode switch,
 * and swapping the composer for a recording bar on every tap is the flicker the
 * spec was written against.
 */
export function recorderBusy(r: Recorder): boolean {
  const s = r.state();
  return s.phase !== "idle" && s.phase !== "pressing";
}

/** Replaces the attach button and text field while a take is in progress. */
export function RecordingBar(props: { recorder: Recorder }): JSX.Element {
  const r = props.recorder;
  const s = (): RecState => r.state();
  const barLabel = (): string => {
    const st = s();
    const kind = "kind" in st ? st.kind : r.mode();
    return kind === "video_note" ? t("chat.record.bar_video") : t("chat.record.bar_voice");
  };
  return (
    <div class="lakko-rec-bar" role="group" aria-label={barLabel()}>
      <Show when={s().phase === "preview" && s()} keyed fallback={<LiveBar recorder={r} />}>
        {(p) => (
          <Show
            when={p.phase === "preview" && p.kind === "voice"}
            fallback={<span style={{ color: "var(--surface-muted)", "font-size": "0.85rem" }}>{t("chat.record.video_ready")}</span>}
          >
            <VoicePreview recorder={r} />
          </Show>
        )}
      </Show>
    </div>
  );
}

function LiveBar(props: { recorder: Recorder }): JSX.Element {
  const r = props.recorder;
  const s = (): RecState => r.state();
  const dx = (): number => {
    const st = s();
    return st.phase === "recording" ? Math.min(0, st.dx) : 0;
  };
  return (
    <>
      {/* Only once something is actually being captured. The pulsing red dot
          is the strongest "this is recording" signal on the bar, and
          `requesting` — where the device is still being opened — is precisely
          the state in which that would be untrue. */}
      <Show when={s().phase !== "requesting"}>
        <span class="lakko-rec-dot" aria-hidden="true" />
      </Show>
      <span aria-live="off">{formatClock(r.elapsedMs())}</span>
      <span class="lakko-level" aria-hidden="true">
        <span style={{ width: `${Math.round(r.level() * 100)}%` }} />
      </span>
      <Show when={s().phase === "requesting"}>
        <span style={{ color: "var(--surface-muted)", "font-size": "0.85rem" }}>{t("chat.record.starting")}</span>
      </Show>
      <Show when={s().phase === "recording"}>
        {/* Cancel only, since the lock target landed (`LockTarget`). Cancel is
            the half with no destination drawn anywhere — the gesture is a slide
            left into nothing — so the words are all the person has. Naming the
            lock too would caption a thing they can already see, which is noise
            in the one line they read while holding a take. */}
        <span
          style={{
            flex: 1,
            "text-align": "center",
            color: "var(--surface-muted)",
            "font-size": "0.85rem",
            transform: `translateX(${dx() / 2}px)`,
            "white-space": "nowrap",
            overflow: "hidden",
          }}
        >
          {r.micSilent() ? SILENT_MIC_NOTICE() : t("chat.record.slide_cancel")}
        </span>
      </Show>
      <Show when={s().phase === "locked"}>
        {/* The locked bar's spare slot. A locked take runs hands-free until it
            is stopped, so a silent one wastes minutes rather than the seconds a
            held take can, which is why the notice is worth the slot here even
            though there is no hint to displace. */}
        <Show when={r.micSilent()} fallback={<span style={{ flex: 1 }} />}>
          <span
            style={{
              flex: 1,
              "text-align": "center",
              color: "var(--surface-muted)",
              "font-size": "0.85rem",
              "white-space": "nowrap",
              overflow: "hidden",
            }}
          >
            {SILENT_MIC_NOTICE()}
          </span>
        </Show>
        <button type="button" onClick={() => r.remove()} aria-label={t("chat.record.delete")}>
          {t("common.delete")}
        </button>
        <button type="button" class="primary" onClick={() => r.stop()} aria-label={t("chat.record.stop")}>
          {t("chat.record.stop_short")}
        </button>
      </Show>
      <Show when={s().phase === "finalizing"}>
        <span class="lakko-spinner" aria-label={t("chat.record.finishing")} />
      </Show>
    </>
  );
}

function VoicePreview(props: { recorder: Recorder }): JSX.Element {
  const r = props.recorder;
  const [playing, setPlaying] = createSignal(false);
  const [position, setPosition] = createSignal(0);
  const audio = new Audio();
  audio.preload = "auto";
  createEffect(() => {
    const p = r.preview();
    if (p !== null && audio.src !== p.url) audio.src = p.url;
  });
  audio.addEventListener("timeupdate", () => setPosition(audio.currentTime * 1000));
  audio.addEventListener("ended", () => {
    setPlaying(false);
    setPosition(0);
  });
  audio.addEventListener("pause", () => setPlaying(false));
  audio.addEventListener("play", () => setPlaying(true));
  onCleanup(() => {
    audio.pause();
    audio.removeAttribute("src");
    audio.load();
  });
  const duration = (): number => r.preview()?.result.durationMs ?? 0;
  const bars = (): number[] => waveformBars(r.preview()?.result.waveform, 40);
  return (
    <>
      <button
        type="button"
        class="lakko-icon-btn"
        aria-label={playing() ? t("chat.record.pause") : t("chat.record.play", { duration: spokenDuration(duration()) })}
        onClick={() => (playing() ? audio.pause() : void audio.play().catch(() => undefined))}
      >
        {playing() ? "❚❚" : "▶"}
      </button>
      <span class="lakko-wave" aria-hidden="true">
        <For each={bars()}>
          {(h, i) => (
            <i
              data-on={barIsPlayed(i(), bars().length, position(), duration())}
              style={{ height: `${Math.round(h * 100)}%` }}
            />
          )}
        </For>
      </span>
      <span>{playing() ? formatClock(position()) : formatTotalClock(duration())}</span>
      <button type="button" onClick={() => r.remove()}>
        {t("common.delete")}
      </button>
      <button type="button" class="primary" onClick={() => r.sendPreview()}>
        {t("chat.composer.send")}
      </button>
    </>
  );
}

/** SVG progress ring drawn over a circle; `fraction` 0..1. */
export function ProgressRing(props: { fraction: number; stroke?: string; width?: number }): JSX.Element {
  const radius = 48;
  const circumference = 2 * Math.PI * radius;
  return (
    <svg class="lakko-ring" viewBox="0 0 100 100" aria-hidden="true">
      <circle
        cx="50"
        cy="50"
        r={radius}
        fill="none"
        stroke={props.stroke ?? "var(--accent)"}
        stroke-width={props.width ?? 3}
        stroke-dasharray={`${circumference}`}
        stroke-dashoffset={`${circumference * (1 - Math.max(0, Math.min(1, props.fraction)))}`}
        stroke-linecap="round"
      />
    </svg>
  );
}

/** UX spec §3 — the circular live preview over the chat while a video note
 *  is being recorded, and the looping preview once it is stopped. */
export function VideoNoteOverlay(props: { recorder: Recorder }): JSX.Element {
  const r = props.recorder;
  const visible = (): boolean => {
    const s = r.state();
    return (
      (s.phase === "recording" || s.phase === "locked" || s.phase === "finalizing" || s.phase === "preview") &&
      s.kind === "video_note"
    );
  };
  return (
    <Show when={visible()}>
      <div class="lakko-vn-overlay" data-holding={r.state().phase === "recording"}>
        <Show when={r.state().phase === "preview"} fallback={<LiveCircle recorder={r} />}>
          <PreviewCircle recorder={r} />
        </Show>
      </div>
    </Show>
  );
}

function LiveCircle(props: { recorder: Recorder }): JSX.Element {
  const r = props.recorder;
  let video: HTMLVideoElement | undefined;
  createEffect(() => {
    const stream = r.liveStream();
    if (video === undefined) return;
    video.srcObject = stream;
    if (stream !== null) void video.play().catch(() => undefined);
  });
  onCleanup(() => {
    if (video !== undefined) video.srcObject = null;
  });
  return (
    <>
      <div class="lakko-vn-circle">
        <video
          ref={video}
          muted
          playsinline
          autoplay
          style={{ transform: r.facing() === "user" ? "scaleX(-1)" : "none" }}
        />
        <ProgressRing fraction={r.elapsedMs() / capFor("video_note")} stroke="var(--danger)" width={4} />
      </div>
      <div class="lakko-vn-controls">
        <span class="lakko-rec-dot" aria-hidden="true" />
        <span>{formatClock(r.elapsedMs())}</span>
        <Show when={r.state().phase === "recording"}>
          {/* Cancel only, for the reason `LiveBar` gives: a video note is held
              with the same gesture on the same button, so the same lock target
              is drawn over this overlay — and the finger is down there, not up
              here beside the circle. */}
          <span style={{ color: "var(--surface-muted)", "font-size": "0.85rem" }}>
            ‹ Slide to cancel
          </span>
        </Show>
        <Show when={r.state().phase === "locked"}>
          <button type="button" onClick={() => r.flipCamera()} aria-label={t("chat.record.flip_camera")}>
            ⟲
          </button>
          <button type="button" onClick={() => r.remove()}>
            {t("common.delete")}
          </button>
          <button type="button" class="primary" onClick={() => r.stop()}>
            {t("chat.record.stop_short")}
          </button>
        </Show>
        <Show when={r.state().phase === "finalizing"}>
          <span class="lakko-spinner" aria-label={t("chat.record.finishing")} />
        </Show>
      </div>
    </>
  );
}

function PreviewCircle(props: { recorder: Recorder }): JSX.Element {
  const r = props.recorder;
  const [withSound, setWithSound] = createSignal(false);
  let video: HTMLVideoElement | undefined;
  return (
    <>
      <button
        type="button"
        class="lakko-vn-circle"
        style={{ padding: 0, border: "none" }}
        aria-label={withSound() ? t("chat.record.pause_video") : t("chat.record.play_video_sound")}
        onClick={() => {
          if (video === undefined) return;
          if (!withSound()) {
            setWithSound(true);
            video.muted = false;
            video.loop = false;
            video.currentTime = 0;
            void video.play().catch(() => undefined);
          } else if (video.paused) {
            void video.play().catch(() => undefined);
          } else {
            video.pause();
          }
        }}
      >
        <video
          ref={video}
          src={r.preview()?.url ?? ""}
          muted
          loop
          autoplay
          playsinline
          onEnded={() => {
            if (video === undefined) return;
            setWithSound(false);
            video.muted = true;
            video.loop = true;
            void video.play().catch(() => undefined);
          }}
        />
        <Show when={!withSound()}>
          <span class="lakko-vn-glyph" aria-hidden="true">🔇</span>
        </Show>
      </button>
      <div class="lakko-vn-controls">
        <span>{formatTotalClock(r.preview()?.result.durationMs ?? 0)}</span>
        <button type="button" onClick={() => r.remove()}>
          {t("common.delete")}
        </button>
        <button type="button" class="primary" onClick={() => r.sendPreview()}>
          {t("chat.composer.send")}
        </button>
      </div>
    </>
  );
}

/** The permission-denied card and the short hints ("Hold to record"). */
export function RecorderNotices(props: { recorder: Recorder }): JSX.Element {
  const r = props.recorder;
  return (
    <>
      <Show when={r.denied()}>
        {(kind) => (
          <div class="lakko-media-card" role="alert">
            <span style={{ flex: 1 }}>{deniedCopy(kind())}</span>
            <button type="button" onClick={() => r.dismissDenied()} style={{ padding: "0.2rem 0.6rem" }}>
              {t("common.dismiss")}
            </button>
          </div>
        )}
      </Show>
      <div aria-live="polite" style={{ "min-height": 0 }}>
        <Show when={r.notice()}>
          {(n) => (
            <span
              class={n() === "cancelled" ? "lakko-shake" : undefined}
              style={{ display: "inline-block", "font-size": "0.8rem", color: "var(--surface-muted)" }}
            >
              {noticeCopy(n())}
            </span>
          )}
        </Show>
      </div>
    </>
  );
}
